/* =====================================================================
 * Conversation memory sweep scheduler
 *
 * Debounces conversation-cache pruning to a 120ms trailing-edge timer so
 * the click hot path (active conversation switch + first render) runs
 * first, and memory management happens on the next tick.
 *
 * Extracted from chatStore.ts. The scheduler uses dependency injection
 * (getState/setState) to avoid a circular import with the Zustand store
 * file.
 * ===================================================================== */

import type { Conversation } from '@auw/workspace-utils';
import { applyConversationMemoryPolicy } from '../domain/conversationMemory';

export interface ConversationMemorySweepDeps {
  getState: () => {
    conversations: Record<string, Conversation>;
    activeId: string | null;
  };
  setState: (patch: { conversations: Record<string, Conversation> }) => void;
}

let conversationMemorySweepTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleConversationMemorySweep(deps: ConversationMemorySweepDeps): void {
  if (conversationMemorySweepTimer) {
    clearTimeout(conversationMemorySweepTimer);
  }
  conversationMemorySweepTimer = setTimeout(() => {
    conversationMemorySweepTimer = null;
    const state = deps.getState();
    const memoryManaged = applyConversationMemoryPolicy(state.conversations, state.activeId);
    if (memoryManaged !== state.conversations) {
      deps.setState({ conversations: memoryManaged });
    }
  }, 120);
}

export function cancelConversationMemorySweep(): void {
  if (conversationMemorySweepTimer) {
    clearTimeout(conversationMemorySweepTimer);
    conversationMemorySweepTimer = null;
  }
}
